import { compareDates, daysBetween, fromEpochDay, maxDate, minDate, toEpochDay, } from './calendarDate.js';
/** A range is well-formed when it covers at least one night. */
export function isValidRange(range) {
    return compareDates(range.start, range.end) < 0;
}
/** Nights covered by the range. */
export function nightsIn(range) {
    return Math.max(0, daysBetween(range.start, range.end));
}
export function totalNights(ranges) {
    return ranges.reduce((sum, range) => sum + nightsIn(range), 0);
}
export function rangesOverlap(a, b) {
    return compareDates(a.start, b.end) < 0 && compareDates(b.start, a.end) < 0;
}
/** The shared nights of both ranges, or `null` when they never coincide. */
export function intersectRanges(a, b) {
    const start = maxDate(a.start, b.start);
    const end = minDate(a.end, b.end);
    return compareDates(start, end) < 0 ? { start, end } : null;
}
/** True when every night of `inner` is also a night of `outer`. */
export function containsRange(outer, inner) {
    return compareDates(outer.start, inner.start) <= 0 && compareDates(outer.end, inner.end) >= 0;
}
export function containsDate(range, date) {
    return compareDates(range.start, date) <= 0 && compareDates(date, range.end) < 0;
}
/** Sorted, non-overlapping equivalent of `ranges`; touching ranges are fused. */
export function normalizeRanges(ranges) {
    const usable = ranges.filter(isValidRange).sort((a, b) => compareDates(a.start, b.start));
    const merged = [];
    for (const range of usable) {
        const last = merged.at(-1);
        if (last !== undefined && compareDates(range.start, last.end) <= 0) {
            merged[merged.length - 1] = { start: last.start, end: maxDate(last.end, range.end) };
            continue;
        }
        merged.push(range);
    }
    return merged;
}
/** Every night present in both normalized sets. */
export function intersectRangeSets(left, right) {
    const result = [];
    for (const a of normalizeRanges(left)) {
        for (const b of normalizeRanges(right)) {
            const overlap = intersectRanges(a, b);
            if (overlap !== null) {
                result.push(overlap);
            }
        }
    }
    return normalizeRanges(result);
}
/** Nights of `range` that no range in `holes` covers. */
export function subtractRanges(range, holes) {
    let remaining = isValidRange(range) ? [range] : [];
    for (const hole of normalizeRanges(holes)) {
        const next = [];
        for (const piece of remaining) {
            if (!rangesOverlap(piece, hole)) {
                next.push(piece);
                continue;
            }
            if (compareDates(piece.start, hole.start) < 0) {
                next.push({ start: piece.start, end: hole.start });
            }
            if (compareDates(hole.end, piece.end) < 0) {
                next.push({ start: hole.end, end: piece.end });
            }
        }
        remaining = next;
    }
    return remaining;
}
/** The smallest range covering every input, or `null` for an empty input. */
export function boundingRange(ranges) {
    const usable = ranges.filter(isValidRange);
    const first = usable[0];
    if (first === undefined) {
        return null;
    }
    return usable.reduce((acc, range) => ({ start: minDate(acc.start, range.start), end: maxDate(acc.end, range.end) }), first);
}
/**
 * Cuts `ranges` at every date in `boundaries`, yielding contiguous slices whose
 * interiors contain no boundary.
 *
 * This is how the planner turns a jumble of overlapping stays and house
 * availabilities into a timeline of segments during which the cast of guests
 * and the set of usable beds are both constant.
 */
export function splitAtBoundaries(ranges, boundaries) {
    const cuts = [...new Set(boundaries.map(toEpochDay))].sort((a, b) => a - b);
    return normalizeRanges(ranges).flatMap((range) => {
        const startDay = toEpochDay(range.start);
        const endDay = toEpochDay(range.end);
        const inner = cuts.filter((cut) => cut > startDay && cut < endDay);
        const points = [startDay, ...inner, endDay];
        const slices = [];
        for (let index = 0; index < points.length - 1; index += 1) {
            slices.push({
                start: fromEpochDay(points[index]),
                end: fromEpochDay(points[index + 1]),
            });
        }
        return slices;
    });
}
/** Every date at which some range in the set starts or stops. */
export function boundariesOf(ranges) {
    const dates = new Set();
    for (const range of ranges) {
        if (isValidRange(range)) {
            dates.add(range.start);
            dates.add(range.end);
        }
    }
    return [...dates].sort(compareDates);
}
export function formatRange(range) {
    return `${range.start} → ${range.end}`;
}
//# sourceMappingURL=dateRange.js.map